import { AlertCircle, ArrowRight, Building2, User } from "lucide-react";
import { useEffect, useMemo, useRef } from "react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import { ColocaAlert } from "@/shared/components/feedback/ColocaAlert";
import { ColocaChoiceCardField } from "@/shared/components/forms/ColocaChoiceCardField";
import { ColocaCountryField } from "@/shared/components/forms/ColocaCountryField";
import { ColocaForm } from "@/shared/components/forms/ColocaForm";
import { ColocaInputField } from "@/shared/components/forms/ColocaInputField";
import { ColocaPasswordField } from "@/shared/components/forms/ColocaPasswordField";
import { ColocaPhoneField } from "@/shared/components/forms/ColocaPhoneField";
import { useZodForm } from "@/shared/components/forms/useZodForm";
import { LEGAL_LINKS } from "@/shared/constants/legal";
import { ONBOARDING_COUNTRY_ISO2 } from "@/shared/data/onboarding-countries";
import { ALL_PHONE_COUNTRIES } from "@/shared/data/phone-countries";
import { HOME_MARKET_ISO2 } from "@/shared/data/phone-country-list";
import useTranslations from "@/shared/lang/useTranslations";

import { ORG_NAME_SUGGESTION_SUFFIX } from "../constants/register.constants";
import { useStickyMirror } from "../hooks/useStickyMirror";
import type { TurnstileController } from "../hooks/useTurnstile";
import type { PreferredLanguage, RegisterRequestDto } from "../types/register.types";
import { createRegisterSchema, type RegisterFormValues } from "../utils/registerSchema";
import { readSignupAttribution } from "../utils/signupAttribution";
import { PasswordStrengthMeter } from "./PasswordStrengthMeter";
import { TurnstileWidget } from "./TurnstileWidget";

interface RegisterFormProps {
  isLoading: boolean;
  error?: string | null;
  emailConflict: boolean;
  preferredLanguage: PreferredLanguage;
  onSubmit: (body: RegisterRequestDto) => void;
  onClearError: () => void;
  turnstile: TurnstileController;
}

// Colombia is preselected on both the country and the phone, not left blank. The phone input is disabled
// until a country resolves (`ColocaPhoneField`), so an empty default made the field unusable on arrival and
// the dial code impossible to see; and the home market is the right guess for most of the people who land
// here. The dial code is read off the catalog rather than typed, so the two can never disagree.
const DEFAULT_COUNTRY_ISO2 = HOME_MARKET_ISO2;
const DEFAULT_DIAL_CODE =
  ALL_PHONE_COUNTRIES.find((country) => country.iso2 === DEFAULT_COUNTRY_ISO2)?.dialCode ?? "";

const EMPTY_VALUES: RegisterFormValues = {
  accountType: "persona",
  user: {
    name: "",
    email: "",
    password: "",
    country: DEFAULT_COUNTRY_ISO2,
    phone: DEFAULT_DIAL_CODE,
  },
  organization: { name: "", email: "" },
};

export function RegisterForm({
  isLoading,
  error,
  emailConflict,
  preferredLanguage,
  onSubmit,
  onClearError,
  turnstile,
}: RegisterFormProps) {
  const { t } = useTranslations("register");
  const { t: tGlobal } = useTranslations("global");

  // Schema locked at mount; remount via `key` prop on language change for
  // fresh resolver with translated Zod messages.
  const schema = useMemo(() => createRegisterSchema((key) => t(key)), [t]);
  const form = useZodForm(schema, EMPTY_VALUES);
  const {
    watch,
    setError,
    clearErrors,
    formState: { isValid, isSubmitting },
  } = form;

  const accountType = watch("accountType");
  const isEmpresa = accountType === "empresa";

  // Sticky mirrors — org name/email prefill from the person until the user edits
  // them. Only relevant for `empresa` (the fields only render there); for
  // `persona` the org side is derived at submit. Org phone/country always mirror
  // the person silently, so they have no dedicated mirror hook.
  const orgEmailMirror = useStickyMirror<RegisterFormValues>({
    form,
    source: "user.email",
    target: "organization.email",
  });
  const orgNameMirror = useStickyMirror<RegisterFormValues>({
    form,
    source: "user.name",
    target: "organization.name",
    derive: (name) => (name.trim() ? `${name.trim()}${ORG_NAME_SUGGESTION_SUFFIX}` : ""),
  });

  const passwordValue = watch("user.password");

  // Strength labels indexed 0..4.
  const strengthLabels = useMemo(
    () =>
      [
        t("pwd_strength_empty"),
        t("pwd_strength_weak"),
        t("pwd_strength_fair"),
        t("pwd_strength_good"),
        t("pwd_strength_strong"),
      ] as readonly [string, string, string, string, string],
    [t],
  );

  const accountTypeItems = useMemo(
    () => [
      {
        value: "persona",
        label: t("account_type_persona_label"),
        description: t("account_type_persona_description"),
        icon: <User className="size-5" />,
      },
      {
        value: "empresa",
        label: t("account_type_empresa_label"),
        description: t("account_type_empresa_description"),
        icon: <Building2 className="size-5" />,
      },
    ],
    [t],
  );

  // Surface backend 409 as inline RHF error on user.email.
  useEffect(() => {
    if (emailConflict) {
      setError("user.email", {
        type: "manual",
        message: t("error_email_conflict"),
      });
    }
  }, [emailConflict, setError, t]);

  // Clear conflict + RHF error when user edits the email field.
  useEffect(() => {
    const sub = form.watch((_values, info) => {
      if (info.name === "user.email" && emailConflict) {
        onClearError();
        clearErrors("user.email");
      }
    });
    return () => sub.unsubscribe();
  }, [form, emailConflict, onClearError, clearErrors]);

  // When the user picks a country, prefill the phone field with that country's
  // dial code (so the input shows "+57 " etc.). We only overwrite if the phone
  // is empty or contains only a previous dial-code prefix — typed numbers are
  // preserved. We track the previous country in a ref so we can detect when a
  // value like "+57" should be replaced vs left alone.
  // Seeded with the preselected country so the mount pass is a no-op: the phone already carries that
  // country's dial code, and an empty seed would make the effect treat it as a country change.
  const previousUserCountry = useRef<string>(DEFAULT_COUNTRY_ISO2);

  const userCountry = watch("user.country");

  useEffect(() => {
    if (!userCountry || userCountry === previousUserCountry.current) return;
    const next = ALL_PHONE_COUNTRIES.find((c) => c.iso2 === userCountry);
    if (!next) return;
    const currentPhone = (form.getValues("user.phone") ?? "").trim();
    const previousDial = previousUserCountry.current
      ? ALL_PHONE_COUNTRIES.find((c) => c.iso2 === previousUserCountry.current)?.dialCode
      : undefined;
    const isJustDialCode =
      !currentPhone || (!!previousDial && currentPhone.replace(/\s/g, "") === previousDial);
    if (isJustDialCode) {
      form.setValue("user.phone", next.dialCode, { shouldValidate: false });
    }
    previousUserCountry.current = userCountry;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userCountry]);

  // Detect the user's browser timezone once on mount; sent with registration so
  // the server saves it as the initial `users.timezone`.
  const browserTimezone = useMemo(() => {
    try {
      return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
    } catch {
      return undefined;
    }
  }, []);

  const handleSubmit = (values: RegisterFormValues) => {
    // Phone values arrive digits-only from the Zod transform (mask separators
    // and leading '+' are stripped in registerSchema). Trust the parsed output.
    const empresa = values.accountType === "empresa";
    const fullName = values.user.name.trim();

    // Org data always sent (backend contract is identical for both types). For
    // `persona` it is derived silently from the person; for `empresa` the name
    // and email come from the visible fields. Phone/country always mirror the
    // person.
    const orgName = empresa ? values.organization.name.trim() : `${fullName}${ORG_NAME_SUGGESTION_SUFFIX}`;
    const orgEmail = empresa ? values.organization.email.trim() : values.user.email.trim();

    const body: RegisterRequestDto = {
      user: {
        full_name: fullName,
        email: values.user.email.trim(),
        password: values.user.password,
        phone_number: values.user.phone,
        preferred_language: preferredLanguage,
        country: values.user.country,
        ...(browserTimezone ? { timezone: browserTimezone } : {}),
      },
      organization: {
        name: orgName,
        email: orgEmail,
        phone: values.user.phone,
        country: values.user.country,
      },
    };
    const attribution = readSignupAttribution();
    if (attribution) body.attribution = attribution;
    onSubmit(body);
  };

  const submitDisabled = !isValid || isLoading || isSubmitting || !turnstile.ready;

  // The page heading renders here, not in `RegisterPage`, because its subtitle depends on `accountType` —
  // RHF state this component owns. A fragment keeps both blocks as direct children of the page's flex
  // container (so the `gap-6` still applies) and keeps the <h1> out of the <form>, where a page-level
  // heading does not belong.
  return (
    <>
      <div className="w-full max-w-md text-center">
        <h1 className="text-2xl font-semibold">{t("page_title")}</h1>
        <p className="text-muted-foreground mt-1 text-sm">
          {isEmpresa ? t("page_subtitle_empresa") : t("page_subtitle_persona")}
        </p>
      </div>

      <ColocaForm form={form} onSubmit={handleSubmit} className="w-full max-w-md">
        {error && !emailConflict && <ColocaAlert variant="destructive" icon={AlertCircle} text={error} />}

        <ColocaChoiceCardField
          name="accountType"
          label={t("account_type_label")}
          items={accountTypeItems}
          columns={2}
        />

        <ColocaInputField
          name="user.name"
          label={t("user_name_label")}
          placeholder={t("user_name_placeholder")}
        />

        <ColocaCountryField
          name="user.country"
          label={t("user_country_label")}
          placeholder="—"
          countries={ONBOARDING_COUNTRY_ISO2}
        />

        {/* No `countries`: the phone takes the curated phone allowlist, which is deliberately NOT the
            residence one above. Somebody we can serve in Colombia may hold a US or Canadian number. */}
        <ColocaPhoneField
          key={`user-phone-${userCountry || "none"}`}
          name="user.phone"
          label={t("user_phone_label")}
          placeholder={t("user_phone_placeholder")}
          defaultCountry={userCountry || undefined}
        />

        <ColocaInputField
          name="user.email"
          label={t("user_email_label")}
          placeholder={t("user_email_placeholder")}
          type="email"
        />

        <ColocaPasswordField
          name="user.password"
          label={t("user_password_label")}
          placeholder={t("user_password_placeholder")}
          autoComplete="new-password"
          showLabel={tGlobal("input_show_password_label")}
          hideLabel={tGlobal("input_hide_password_label")}
          description={
            <PasswordStrengthMeter
              value={passwordValue ?? ""}
              labels={strengthLabels}
              ariaLabel={t("pwd_strength_aria_label")}
            />
          }
        />

        {isEmpresa && (
          <fieldset className="space-y-4 border-t pt-5">
            <legend className="text-sm font-semibold">{t("section_organization_heading")}</legend>

            <ColocaInputField
              name="organization.name"
              label={t("org_name_label")}
              placeholder={t("org_name_placeholder")}
              description={orgNameMirror.isMirrored ? t("hint_mirror_org_name") : undefined}
            />

            <ColocaInputField
              name="organization.email"
              label={t("org_email_label")}
              placeholder={t("user_email_placeholder")}
              type="email"
              description={orgEmailMirror.isMirrored ? t("hint_mirror_email") : undefined}
            />
          </fieldset>
        )}

        <TurnstileWidget turnstile={turnstile} />

        <div className="flex flex-col-reverse md:flex-row items-center gap-4">
          <p className="text-sm text-muted-foreground block grow">
            {t("terms_text_prefix")}
            <a
              href={LEGAL_LINKS[preferredLanguage].terms}
              target="_blank"
              rel="noopener noreferrer"
              className="text-foreground underline"
            >
              {t("terms_link")}
            </a>
            {t("terms_text_middle")}
            <a
              href={LEGAL_LINKS[preferredLanguage].privacy}
              target="_blank"
              rel="noopener noreferrer"
              className="text-foreground underline"
            >
              {t("privacy_link")}
            </a>
            {t("terms_text_suffix")}
          </p>

          <ColocaButton
            type="submit"
            variant="primary"
            iconTrailing={ArrowRight}
            loading={isLoading}
            disabled={submitDisabled}
            className="w-full md:w-auto md:grow"
          >
            {isLoading ? t("submit_loading") : t("submit_button")}
          </ColocaButton>
        </div>
      </ColocaForm>
    </>
  );
}
